import React from "react";
import { END_OF_DAY_LOAD_ROUTE_COLOR } from "../../utils/routeModelsData.js";

const badgeClass = "inline-block rounded-[4px] border border-yellow-600 px-1.5 font-semibold text-slate-900";
const badgeStyle = { backgroundColor: END_OF_DAY_LOAD_ROUTE_COLOR };

const dateLabel = value => value ? new Intl.DateTimeFormat("fr-CA", {
  day: "numeric", month: "long", timeZone: "UTC",
}).format(new Date(`${value}T12:00:00Z`)) : "—";

const shortDateLabel = value => value ? new Intl.DateTimeFormat("fr-CA", {
  day: "numeric", month: "short", timeZone: "UTC",
}).format(new Date(`${value}T12:00:00Z`)) : "—";

// A validated PM loading follows its delivery for billing; the consolidator never has to come back.
const PM_STATES = {
  pending: { tone: "bg-amber-100 text-amber-900", label: visit => `PM en attente · ${shortDateLabel(visit.pm.target_dispatch_date)}` },
  confirmed: { tone: "bg-slate-100 text-slate-700", label: () => "PM confirmé" },
  review: { tone: "bg-red-100 text-red-800", label: () => "PM à revoir" },
};

export const pmSourceVisits = sheet => (sheet?.visits || []).filter(visit => visit.pm?.role === "source" && PM_STATES[visit.pm_contribution_state]);

export function PmStatusBadge({ visit }) {
  const state = PM_STATES[visit.pm_contribution_state];
  if (!state) return null;
  return <span className={`whitespace-nowrap rounded-[4px] px-1.5 py-0.5 text-xs ${state.tone}`}>{state.label(visit)}</span>;
}

function pendingNotice(visit) {
  if (visit.pm_contribution_state || visit.pm_delivery?.state !== "waiting") return null;
  if (visit.pm_delivery.orders?.some(order => order.status === "ambiguous")) return <div className="relative z-10 mt-1 w-max whitespace-nowrap text-red-700" role="alert">
    Plusieurs relevés validés livrent ce chargé PM : rouvrez celui qui ne l’a pas livré, mettez 0 dans « Réel (t) » de sa commande, puis validez.
  </div>;
  return <div className="relative z-10 mt-1 w-max whitespace-nowrap" role="status">
    Livraison du {visit.pm.target_period === "evening" ? "soir du " : ""}<span className="whitespace-nowrap">{dateLabel(visit.pm.target_dispatch_date)}</span> à valider : les minutes PM seront réparties automatiquement selon le tonnage livré ({visit.orders.map(order => order.label).join(", ")}).
  </div>;
}

export default function PmVisitDetails({ visit }) {
  if (visit.pm?.role === "source") return <div className="mt-1 text-[11px] leading-4 text-slate-600">
    <span className="flex flex-wrap items-center gap-1"><span className={badgeClass} style={badgeStyle}>Chargé PM</span><PmStatusBadge visit={visit} /></span>
    <div>Livraison prévue le {visit.pm.target_period === "evening" ? "soir du " : ""}<span className="whitespace-nowrap">{dateLabel(visit.pm.target_dispatch_date)}</span></div>
    <div>Minutes rattachées au <span className="whitespace-nowrap">{dateLabel(visit.pm.target_dispatch_date)}</span></div>
    {pendingNotice(visit)}
    {visit.pm_review_reason ? <div className="relative z-10 mt-1 w-max whitespace-nowrap text-red-700" role="alert">{visit.pm_review_reason}</div> : null}
  </div>;
  if (visit.kind !== "delivery") return null;
  // Une seule mention par date de chargement, même si plusieurs commandes sont livrées à cet arrêt.
  const sourceDates = [...new Set(visit.orders.filter(order => order.pm_loading_reference)
    .map(order => order.pm_loading_reference.source_dispatch_date))];
  return sourceDates.map(sourceDate => <div key={sourceDate || "unknown"}
    className="mt-1 text-[11px] leading-4 text-slate-600">
    <span className={badgeClass} style={badgeStyle}>Chargé PM le <span className="whitespace-nowrap">{dateLabel(sourceDate)}</span></span>
  </div>);
}
